import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";

const LINK_KEYS = [
  { key: "services", href: "#services" },
  { key: "process", href: "#process" },
  { key: "results", href: "#results" },
  { key: "about", href: "#about" },
  { key: "contact", href: "#contact" },
];

const scrollTo = (hash) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      className={`flex items-center rounded-full border border-slate-200 bg-white p-0.5 ${className}`}
    >
      {["en", "sv"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`font-mono-alt rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
            lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-slate-300/70 bg-[#E8EDF4]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <button data-testid="nav-logo" onClick={() => scrollTo("#top")} className="flex items-center gap-3">
          <Logo />
          <span className="font-display text-lg font-bold tracking-tight text-slate-900">
            NORCEV
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINK_KEYS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.key}`}
              onClick={() => scrollTo(l.href)}
              className="font-mono-alt text-xs uppercase tracking-[0.2em] text-slate-600 transition-colors duration-300 hover:text-blue-700"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div className="flex items-center gap-2" data-testid="nav-status">
            <span className="h-2 w-2 rounded-full bg-teal-500 animate-pulse-dot" />
            <span className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-teal-700">
              {t.nav.status}
            </span>
          </div>
          <LangToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => scrollTo("#contact")}
            className="rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-[0_8px_24px_rgba(29,78,216,0.25)]"
          >
            {t.nav.cta}
          </button>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-slate-800"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-slate-300/70 bg-[#E8EDF4]/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-1 px-6 py-6">
            {LINK_KEYS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-link-${l.key}`}
                onClick={() => {
                  setOpen(false);
                  scrollTo(l.href);
                }}
                className="py-3 text-left font-mono-alt text-sm uppercase tracking-[0.2em] text-slate-700 hover:text-blue-700"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => {
                setOpen(false);
                scrollTo("#contact");
              }}
              className="mt-4 rounded-full bg-blue-700 px-5 py-3 text-sm font-semibold text-white"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
};
